// RsvpTracker.tsx
import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import SearchBar from "../../components/SearchBar";
import { Box, Tab, Tabs } from "@mui/material";
import CustomPagination from "../../components/CustomPagination";
import RsvpTable from "./rsvpTable";
import { isValidInput } from "../../utils/validations";
import { useLocation, useNavigate } from "react-router-dom";
import { useLazyGetCategoryQuery } from "../../service/category";
import {
  useClearBookingBadgeMutation,
  useLazyAllEmployeesQuery,
  useLazyGetBookingsQuery,
} from "../../service/booking";
import { BookingListType, Categories } from "../../types/General";
import { showError } from "../../constants/toasts";
import { BOOKING_STATUS } from "../../utils/enums";
import Loader from "../../constants/Loader";
import dayjs, { Dayjs } from "dayjs";
import BookingFilters from "../../components/BookingFilters";
import { useLazyAllMembersQuery } from "../../service/user";
import useAuth from "../../hooks/useAuth";
import { removeFromStorage, STORAGE_KEYS } from "../../helpers";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import {
  resetBookingFilters,
  setBookingFilters,
} from "../../reducers/bookingFiltersSlice";
import { getSignalRConnection } from "../../utils/socket";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
interface EmployeeType {
  email: string;
  id: string;
  name: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function RsvpTracker() {
  const dispatch = useAppDispatch();
  const bookingFilters = useAppSelector(
    (state: RootState) => state.bookingFilters
  );
  const [getCategory] = useLazyGetCategoryQuery();
  const [getBookings] = useLazyGetBookingsQuery();
  const [getUsers] = useLazyAllMembersQuery();
  const [employees] = useLazyAllEmployeesQuery();
  const [clearBookingBadge] = useClearBookingBadgeMutation();

  const location = useLocation();
  const { state } = location;
  const categoryId = state?.categoryId || "";
  const status = state?.status || "";
  const categoryTab = state?.categoryTab || "";
  const userData = useAuth();

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [valueTabs, setValueTabs] = React.useState(0);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(10);
  const [page, setPage] = useState(1);
  const [categoryData, setCategoryData] = useState<Categories[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    ""
  );
  const [employeeData, setEmployeeData] = useState<EmployeeType[]>([]);
  const [users, setUsers] = useState([]);
  const [currentOrder, setCurrentOrder] = useState<string>("DateCreated_desc");

  const [bookingData, setBookingData] = useState<BookingListType[]>([]);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setPage(1);
    setValueTabs(newValue);
    if (newValue === 0) {
      // If "All" tab is selected
      setSelectedCategoryId("all");
    } else {
      // Subtract 1 because categoryData starts after "All"
      const categoryId = categoryData?.[newValue - 1]?.id || null;
      setSelectedCategoryId(categoryId);
    }
  };
  const navigate = useNavigate();

  const [valueTabs1, setValueTabs1] = React.useState(0);
  const handleChangeTabs1 = (event: React.SyntheticEvent, newValue: number) => {
    setPage(1);
    setValueTabs1(newValue);
  };

  const tabCategories = ["All", "Dining", "Event", "Experience"];
  const statusTabs = ["New", "In Progress", "Confirmed", "Completed"];

  const currentCategory = tabCategories[valueTabs];
  const currentStatus = statusTabs[valueTabs1];

  const getBookingsApi = async (clearAll?: any) => {
    try {
      const body: Record<string, any> = {
        ...(bookingFilters.startRange && !clearAll
          ? {
              startRange: dayjs(bookingFilters.startRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(bookingFilters.endRange && !clearAll
          ? {
              endRange: dayjs(bookingFilters.endRange).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(bookingFilters.bookingDateFrom && !clearAll
          ? {
              bookingDateFrom: dayjs(bookingFilters.bookingDateFrom).format(
                "YYYY-MM-DDTHH:mm:ss"
              ),
            }
          : {}),
        ...(bookingFilters.userId && !clearAll
          ? { userId: bookingFilters.userId }
          : {}),
        ...(bookingFilters.memberShipType && !clearAll
          ? { memberShipType: bookingFilters.memberShipType }
          : {}),
        ...(bookingFilters.employeeId && !clearAll
          ? { employeeId: bookingFilters.employeeId }
          : {}),
        ...(bookingFilters.numberofGuest && !clearAll
          ? { numberofGuest: bookingFilters.numberofGuest }
          : {}),
        ...(bookingFilters.bookingNumberofDays && !clearAll
          ? { bookingNumberofDays: 30 }
          : {}),
        ...(bookingFilters.isTodayBooking && !clearAll
          ? { isTodayBooking: bookingFilters.isTodayBooking }
          : {}),
      };

      setLoading(true);
      const response = await getBookings({
        page: page,
        search: debouncedSearchTerm,
        categoryId: selectedCategoryId,
        status:
          valueTabs1 === 0
            ? BOOKING_STATUS.NEW_BOOKING
            : valueTabs1 === 1
              ? BOOKING_STATUS.PENDING
              : valueTabs1 === 2
                ? BOOKING_STATUS.CONFIRM
                : valueTabs1 === 3
                  ? BOOKING_STATUS.COMPLETE
                  : "",
        sortOrder: currentOrder,
        body,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setBookingData(response?.data?.result?.items || []);
        setCount(response?.data?.result?.totalCount || 0);
        setCurrentOrder(response?.data?.sortOrder || "");
      } else {
        setBookingData([]);
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    try {
      const response = await getCategory({ search: "" }).unwrap();
      if (response.status === 200) {
        setCategoryData(response?.data || []);
        // setCount(response?.data?.totalCount);
        if (categoryId) {
          setSelectedCategoryId(categoryId);
        } else {
          setSelectedCategoryId("all");
        }
      } else {
        setCategoryData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getEmployeesApi = async () => {
    try {
      const response = await employees({}).unwrap();
      if (response.status === 200) {
        setEmployeeData(response?.data || []);
      } else {
        setEmployeeData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const clearBookingBadgeApi = async () => {
    try {
      const response = await clearBookingBadge({}).unwrap();
      if (response.status === 200) {
        removeFromStorage(STORAGE_KEYS.bookingCount);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getUsersApi = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        search: "",
      }).unwrap();

      setLoading(false);

      if (response.status === 200) {
        setUsers(response?.data || []);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setBookingFilters({ [key]: value }));
  };

  const handleClearAll = () => {
    dispatch(resetBookingFilters());
  };

  useEffect(() => {
    const connection = getSignalRConnection();
    if (!connection) return;

    const handleBookingCount = (data: any) => {
      getBookingsApi();
    };

    connection.on("NewBookingCreated", handleBookingCount);

    return () => {
      connection.off("NewBookingCreated ", handleBookingCount);
    };
  }, []);

  useEffect(() => {
    if (selectedCategoryId && currentOrder) {
      getBookingsApi();
    }
  }, [selectedCategoryId, page, debouncedSearchTerm, valueTabs1, currentOrder]);

  useEffect(() => {
    getCategoryApi();
    if (categoryId) {
      setSelectedCategoryId(categoryId);
    }

    if (categoryTab) {
      setValueTabs(categoryTab);
    }

    if (status) {
      setValueTabs1(status);
    }
  }, []);

  useEffect(() => {
    getEmployeesApi();
    getUsersApi();
    clearBookingBadgeApi();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title d_flex">
        <div className="left_s">
          <h1>Booking & RSVP Tracker</h1>
        </div>
        <div className="right_s">
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search...."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm("")}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
          {/* <Button
            className="btn btn_primary"
            onClick={() => navigate("/manage-booking-resvp/add-user-booking")}
          >
            Add User
          </Button> */}
          <BookingFilters
            startDate={bookingFilters.startRange}
            endDate={bookingFilters.endRange}
            setStartDate={(date) => handleFilterChange("startRange", date)}
            setEndDate={(date) => handleFilterChange("endRange", date)}
            bookingDateFrom={bookingFilters.bookingDateFrom}
            setBookingDateFrom={(value) =>
              handleFilterChange("bookingDateFrom", value)
            }
            selectedUsers={bookingFilters.userId}
            setSelectedUsers={(value) => handleFilterChange("userId", value)}
            memberShipType={bookingFilters.memberShipType}
            setMemberShipType={(value) =>
              handleFilterChange("memberShipType", value)
            }
            selectedCategory={bookingFilters.categoryId}
            setSelectedCategory={(value) =>
              handleFilterChange("categoryId", value)
            }
            internalData={[]}
            bookingStatus={bookingFilters.bookingStatus}
            setBookingStatus={(value) =>
              handleFilterChange("bookingStatus", value)
            }
            selectedEmployee={bookingFilters.employeeId}
            setSelectedEmployee={(value) =>
              handleFilterChange("employeeId", value)
            }
            numberOfGuests={bookingFilters.numberofGuest}
            setNumberOfGuests={(value) =>
              handleFilterChange("numberofGuest", value)
            }
            bookingNumberofDays={bookingFilters.bookingNumberofDays}
            setBookingNumberofDays={(value) =>
              handleFilterChange("bookingNumberofDays", value)
            }
            isTodayBooking={bookingFilters.isTodayBooking}
            setIsTodayBooking={(value) =>
              handleFilterChange("isTodayBooking", value)
            }
            category={[]}
            users={users}
            employeeData={employeeData}
            handleSubmit={getBookingsApi}
            noCategory={true}
            noStatus={true}
            handleClearFilters={handleClearAll}
          />
        </div>
      </div>

      <Box className="custom_tabs2 mt_40 mb_30" sx={{ width: "100%" }}>
        <Tabs
          value={valueTabs}
          onChange={handleChangeTabs}
          aria-label="basic tabs example"
          className="custom_tabs_links"
        >
          <Tab key="all" label="All" {...a11yProps(0)} />

          {categoryData?.map((item, i) => (
            <Tab key={item.id} label={item?.name} {...a11yProps(i + 1)} />
          ))}
        </Tabs>
      </Box>

      <Box className="custom_tabs1 mb_30" sx={{ width: "100%" }}>
        <Tabs
          value={valueTabs1}
          onChange={handleChangeTabs1}
          className="custom_tabs_links"
        >
          <Tab label="New Bookings" {...a11yProps(0)} />
          <Tab label="In progress" {...a11yProps(1)} />
          <Tab label="Confirmed" {...a11yProps(2)} />
          <Tab label="Completed" {...a11yProps(3)} />
        </Tabs>
      </Box>

      <CustomTabPanel value={valueTabs1} index={valueTabs1}>
        <RsvpTable
          bookingData={bookingData}
          category={currentCategory}
          statusFilter={currentStatus}
          getBookingsApi={getBookingsApi}
          valueTabs={valueTabs}
          valueTabs1={valueTabs1}
          categoryId={selectedCategoryId}
          sortOrder={currentOrder}
          setSortOrder={setCurrentOrder}
        />
      </CustomTabPanel>

      {bookingData?.length ? (
        <CustomPagination
          currentPage={page - 1}
          totalPages={Math.ceil(count / 10)}
          count={count}
          pageSize={10}
          onPageChange={(page) => setPage(page + 1)}
        />
      ) : null}
    </MainContainer>
  );
}

export default RsvpTracker;
